// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { split } from "@transformers/schema";
import { removeNullAndUndefinedProps } from "@transformers/object";
import actionCreator from "@modules/service/actionCreator";
import * as selectors from "@modules/service/selectors";
import { Button, Col, Row, Icons } from "@components";
import { RtwNotifications } from "@modules/service/components";
import type { StepProps } from "@components";
import type { Rtw } from "@modules/service/reducer/rtw/types";

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Rtw,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetRtwDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepRecipients extends React.Component<Props> {
  form: *;
  validateNotifications: *;

  componentDidMount() {
    if (this.props.schema) {
      this.setValidators();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.setValidators();
    }
  }

  setValidators = () => {
    this.validateNotifications = new Ajv().compile(this.schema.notifications);
  };

  get data() {
    const draft = this.props.draft?.content || {};
    return {
      notifications: {
        ...(draft.managerInstructions !== undefined
          ? { managerInstructions: draft.managerInstructions }
          : {}),
        ...(draft.overdueRtwInstructions !== undefined
          ? { overdueRtwInstructions: draft.overdueRtwInstructions }
          : {}),
        attachAbsenceReport: true,
      },
      notificationAttachments: {
        attachments: draft.attachments || null,
      },
    };
  }

  get schema() {
    return {
      notifications: split(this.props.schema, [
        "managerInstructions",
        "overdueRtwInstructions",
        "attachAbsenceReport",
      ]),
      attachments: {},
    };
  }

  isValid = (data?: Object) => {
    if (!this.validateNotifications) {
      return false;
    }

    const cleanNotifications = removeNullAndUndefinedProps(this.data.notifications);

    const isValid = this.validateNotifications(cleanNotifications);
    if (process.env.NODE_ENV !== "production" && !isValid) {
      console.warn("isValid errors", this.validateNotifications.errors);
    }
    return isValid;
  };

  handleFormRef = (ref: *) => {
    this.form = ref;
  };

  submitForm = () => {
    this.submitNotifications({ formData: (this.data || {}).notifications });
  };

  submitNotifications = (formData: Object) => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(this.data, true);
    }
  };

  updateNotifications = (formData: Object) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        ...formData,
      },
    });
  };

  updateNotificationAttachments = (data: Array<*>) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        notificationAttachments: data,
      },
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12">
            {this.schema && this.data ? (
              <RtwNotifications
                onRef={this.handleFormRef}
                data={this.data.notifications}
                schema={this.schema.notifications}
                onChange={this.updateNotifications}
                onSubmit={this.submitNotifications}
              />
            ) : null}
          </Col>
        </Row>
        <Row className="mt-4 w-100 d-flex justify-content-center">
          <Col sm="12" className="mb-4">
            <h6>{this.props.t("RETURN_TO_WORK_DETAILS_NOTIFICATION_ATTACHMENTS")}</h6>
          </Col>
          <Col sm={12} className="d-flex justify-content-start">
            <Icons.QuestionCircleFilledIcon
              color="chatbubble-background"
              className="fill no-stroke ml-2 question-circle-icon medium mr-2"
            />
            {this.props.t("RETURN_TO_WORK_DETAILS_ADD_ATTACHMENTS_INFO")}
          </Col>
        </Row>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button
              materialIcon="fast_forward"
              onClick={this.submitForm}
              disabled={!this.isValid(this.data)}
            >
              {this.props.t("NEXT")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: selectors.getRtwSchema(state),
    draft: selectors.getRtwDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetRtwDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(StepRecipients);
